import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, fieldErrors, unwrap } from '../../api/client';
import type { User } from '../../api/types';
import { EmployeeSelect } from '../../components/pickers';
import { TokenPanel } from '../../components/TokenPanel';
import { Badge, Button, Card, CardBody, CardHeader, Checkbox, DataList, Dialog, EmptyState, ErrorBox, Field, Input, Loading, PageHeader, Select, Table, Td, Th, Tr, useToast } from '../../components/ui';
import { formatDateTime, ROLE_LABELS } from '../../lib/format';

type Role = User['role'];

function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => unwrap(api.GET('/api/roles')), staleTime: Infinity, select: (r) => r.data });
}

export function UserListPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const users = useQuery({
    queryKey: ['users', showInactive],
    queryFn: () => unwrap(api.GET('/api/users', { params: { query: { active: showInactive ? undefined : 'true' } } })),
  });
  const roles = useRoles();

  return (
    <>
      <PageHeader
        title="Benutzer"
        subtitle="Wer das System nutzen darf. Anmeldung per Cloudflare Access über die E-Mail-Adresse."
        actions={
          <>
            <Checkbox label="Inaktive anzeigen" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Benutzer anlegen
            </Button>
          </>
        }
      />
      <Card className="mb-5">
        {users.isPending ? (
          <Loading />
        ) : users.error ? (
          <ErrorBox error={users.error} className="m-4" />
        ) : users.data.data.length === 0 ? (
          <EmptyState title="Keine Benutzer" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>E-Mail</Th>
                <Th>Rolle</Th>
                <Th>Mitarbeiter</Th>
                <Th>Tokens</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {users.data.data.map((u) => (
                <Tr key={u.id} onClick={() => navigate(`/users/${u.id}`)}>
                  <Td className="font-medium text-brand-700">{u.display_name}</Td>
                  <Td>{u.email}</Td>
                  <Td>
                    <Badge tone={u.role === 'admin' ? 'violet' : u.role === 'it' ? 'blue' : 'gray'}>{ROLE_LABELS[u.role]}</Badge>
                  </Td>
                  <Td>{u.employee?.short_code ?? '–'}</Td>
                  <Td>{u.active_token_count}</Td>
                  <Td>{u.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {roles.data && (
        <Card>
          <CardHeader title="Rollen" />
          <CardBody className="grid gap-4 md:grid-cols-3">
            {roles.data.map((r) => (
              <div key={r.role}>
                <div className="font-medium">{r.label}</div>
                <p className="text-sm text-slate-600">{r.description}</p>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
      <UserDialog user={null} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

export function UserDetailPage() {
  const { ref = '' } = useParams();
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ['users', 'detail', ref], queryFn: () => unwrap(api.GET('/api/users/{ref}', { params: { path: { ref } } })) });

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const u = q.data;

  return (
    <>
      <PageHeader
        back={
          <Link to="/users" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Benutzer
          </Link>
        }
        title={u.display_name}
        subtitle={
          <span className="flex items-center gap-2">
            {u.email} <Badge>{ROLE_LABELS[u.role]}</Badge> {u.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}
          </span>
        }
        actions={
          <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        }
      />
      <div className="space-y-5">
        <Card>
          <CardBody>
            <DataList
              items={[
                ['Mitarbeiter', u.employee ? `${u.employee.short_code} – ${u.employee.name}` : null],
                ['Angelegt', formatDateTime(u.created_at)],
                ['Letzte Token-Nutzung', u.last_token_use_at ? formatDateTime(u.last_token_use_at) : null],
              ]}
            />
          </CardBody>
        </Card>
        <TokenPanel
          queryKey={['users', 'tokens', u.id]}
          description="Tokens für Skripte dieses Benutzers. Menschen melden sich im Betrieb per Cloudflare Access an."
          list={() => unwrap(api.GET('/api/users/{ref}/tokens', { params: { path: { ref: u.id } } }))}
          create={(body) => unwrap(api.POST('/api/users/{ref}/tokens', { params: { path: { ref: u.id } }, body }))}
          revoke={(id) => unwrap(api.DELETE('/api/users/{ref}/tokens/{tokenId}', { params: { path: { ref: u.id, tokenId: id } } }))}
        />
      </div>
      <UserDialog user={u} open={editing} onClose={() => setEditing(false)} />
    </>
  );
}

function UserDialog({ user, open, onClose }: { user: User | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: '', display_name: '', role: 'read_only' as Role, employee: '', is_active: true });
  useEffect(() => {
    if (open)
      setF({
        email: user?.email ?? '',
        display_name: user?.display_name ?? '',
        role: user?.role ?? 'read_only',
        employee: user?.employee?.short_code ?? '',
        is_active: user?.is_active ?? true,
      });
  }, [open, user]);

  const m = useMutation({
    mutationFn: () =>
      user
        ? unwrap(
            api.PATCH('/api/users/{ref}', {
              params: { path: { ref: user.id } },
              body: { display_name: f.display_name.trim(), role: f.role, is_active: f.is_active, employee: f.employee || null },
            }),
          )
        : unwrap(api.POST('/api/users', { body: { email: f.email.trim(), display_name: f.display_name.trim(), role: f.role, employee: f.employee || undefined } })),
    onSuccess: (u) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      toast(user ? 'Gespeichert' : `${u.display_name} angelegt`);
      onClose();
      if (!user) navigate(`/users/${u.id}`);
    },
  });
  const errs = fieldErrors(m.error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={user ? 'Benutzer bearbeiten' : 'Benutzer anlegen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.display_name.trim() || (!user && !f.email.trim())}>
            Speichern
          </Button>
        </>
      }
    >
      <Field label="E-Mail" required error={errs.email} hint="Muss der Anmelde-E-Mail bei Cloudflare Access entsprechen">
        {(p) => <Input {...p} type="email" disabled={!!user} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}
      </Field>
      <Field label="Anzeigename" required error={errs.display_name}>
        {(p) => <Input {...p} value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} />}
      </Field>
      <Field label="Rolle">
        {(p) => (
          <Select {...p} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>
            {(['read_only', 'it', 'admin'] as const).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Verknüpfter Mitarbeiter" hint="Optional">
        {(p) => <EmployeeSelect {...p} value={f.employee} onChange={(v) => setF({ ...f, employee: v })} />}
      </Field>
      {user && <Checkbox label="Aktiv" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />}
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
